import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import i18n from 'i18next';
import { processRequest } from '../../services/Api';
import { historyPush } from '../../services/History';
import * as quizEditorActions from './Actions';
import * as errorHandlerActions from '../errorHandler/Actions';
import * as notificationActions from '../notification/Actions';
import * as nextExerciseDetailsActions from '../../pagesNext/exercise/Actions';
import { IS_DESKTOP_VIEW } from '../../services/Constants';
import { quizData, quizFullData, selectedQuestionData } from './Selectors';
import { quizEditorActionTypes, getStageByType } from './Constants';

export default function* () {
	yield all([
		yield takeLatest(
			quizEditorActionTypes.GET_QUIZ_QUESTIONS_PANEL,
			handleQuizQuestionsPanelRequest,
		),
		yield takeLatest(
			quizEditorActionTypes.CHANGE_QUESTION_POSITION,
			handleChangeQuizQuestionPositionRequest,
		),
		yield takeLatest(
			quizEditorActionTypes.CREATE_QUIZ_QUESTION_REQUEST,
			handleCreateQuizQuestionRequest,
		),
		yield takeLatest(
			quizEditorActionTypes.GET_QUIZ_QUESTION_REQUEST,
			handleGetQuizQuestionRequest,
		),
		yield takeLatest(
			quizEditorActionTypes.MANAGE_QUIZ_QUESTION_REQUEST,
			handleManageQuizQuestionRequest,
		),
		yield takeLatest(
			quizEditorActionTypes.UPDATE_QUIZ_QUESTION_REQUEST,
			handleUpdateQuizQuestionRequest,
		),
		yield takeLatest(quizEditorActionTypes.PREVIEW_QUIZ_REQUEST, handlePreviewQuizRequest),
		yield takeLatest(quizEditorActionTypes.PUBLISH_QUIZ_REQUEST, handlePublishQuizRequest),
	]);
}

const getFilteredIndex = (list, id) => {
	let currentElIndex = 0;
	if (list.length)
		list.forEach((item, index) => {
			if (item.id === id) currentElIndex = index;
		});

	return currentElIndex;
};

export function* handleQuizQuestionsPanelRequest({ payload }) {
	try {
		const { params } = payload || {};
		const { id, type, questionId } = params || {};

		const { data } = yield call(processRequest, 'quizzes', 'GET', {
			type: getStageByType(type),
			id,
		});

		const { questions, last_modified_at } = data.quiz || {};

		if (questions && questions[0]) {
			const isQuestionPresent =
				questionId &&
				questions.find((item) => item.id.toString() === questionId.toString());

			if (IS_DESKTOP_VIEW || questionId) {
				yield put(
					quizEditorActions.getQuizQuestion(
						isQuestionPresent ? isQuestionPresent.id : questions[0].id,
					),
				);
			}
		}

		yield put(quizEditorActions.getQuizQuestionsPanelSuccess(data.quiz));
		yield put(quizEditorActions.setUpdateTime(last_modified_at));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(quizEditorActions.getQuizQuestionsPanelError());
	}
}

export function* handleChangeQuizQuestionPositionRequest({ payload }) {
	const { data: queries } = payload || {};

	try {
		const { data } = yield call(processRequest, `questions/${queries.questionId}`, 'PATCH', {
			question: { position: queries.position },
		});

		const prevQuizData = yield select(quizData);

		const newQuestionsList = prevQuizData.questions.filter(
			(item) => item.id.toString() !== queries.questionId.toString(),
		);
		newQuestionsList.splice(queries.position - 1, 0, data.question);
		yield put(quizEditorActions.updateQuestionsList(newQuestionsList));
	} catch (e) {
		const { response } = e;
		const { data } = response || {};
		if (queries.errorCallback) queries.errorCallback();
		yield put(
			errorHandlerActions.handleError(e, 'notification', {
				code: 422,
				message: data.error,
			}),
		);
	}
}

export function* handleCreateQuizQuestionRequest({ payload }) {
	try {
		const { data: formData, callback } = payload || {};

		const { data } = yield call(processRequest, `questions`, 'POST', {
			question: formData,
		});

		const prevQuizData = yield select(quizData);

		const {
			edited_after_quiz_publish,
			icon_name,
			id,
			position,
			question,
			status,
			stream_ids,
			title,
			valid,
		} = data.question || {};

		const panelData = {
			edited_after_quiz_publish,
			icon_name,
			id,
			position,
			question,
			status,
			stream_ids,
			title,
			valid,
		};

		yield put(
			quizEditorActions.createQuizQuestionSuccess({
				...prevQuizData,
				questions: [...prevQuizData.questions, panelData],
			}),
		);

		if (callback) callback(id);
		yield put(quizEditorActions.getQuizQuestion(id));
		yield put(quizEditorActions.setUpdateTime(new Date()));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(quizEditorActions.createQuizQuestionError());
	}
}

export function* handleGetQuizQuestionRequest({ payload }) {
	try {
		const { id } = payload || {};

		const { data } = yield call(processRequest, `questions/${id}`);

		yield put(quizEditorActions.selectQuestion(data.question));
		yield put(quizEditorActions.getQuizQuestionSuccess());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(quizEditorActions.getQuizQuestionError());
	}
}

export function* handleManageQuizQuestionRequest({ payload }) {
	try {
		const { id, type, callback } = payload || {};
		const requestType = type === 'remove' ? 'DELETE' : 'POST';

		const { data } = yield call(
			processRequest,
			`questions/${id}${type !== 'remove' ? `/${type}` : ''}`,
			requestType,
		);

		const prevQuizData = yield select(quizData);
		const selectedQuestion = yield select(selectedQuestionData);
		const filteredQuestions = prevQuizData.questions.filter((item) => !item.hide);

		// prettier-ignore
		const newQuestionsList =
			type === 'remove' && !data.question
				? prevQuizData.questions.filter((question) => question.id !== id)
				: type === 'revert' || (type === 'remove' && !!data.question)
					? prevQuizData.questions.map((question) =>
						question.id === id ? data.question : question,
				  )
					: [...prevQuizData.questions, data.question];

		const filteredNewQuestionsList = newQuestionsList.filter((item) => !item.hide);

		const formattedList = filteredNewQuestionsList.length
			? filteredQuestions
			: newQuestionsList;

		if (
			selectedQuestion.id === id &&
			newQuestionsList.length &&
			type !== 'revert' &&
			!(!!data.question && type === 'remove') &&
			!(type === 'remove' && !IS_DESKTOP_VIEW)
		) {
			yield put(
				quizEditorActions.getQuizQuestion(
					type === 'duplicate'
						? data.question.id
						: formattedList[getFilteredIndex(formattedList, id) + 1]
						? formattedList[getFilteredIndex(formattedList, id) + 1].id
						: formattedList[getFilteredIndex(formattedList, id) - 1].id,
				),
			);
		}

		if (selectedQuestion.id !== id && type === 'duplicate') {
			yield put(quizEditorActions.getQuizQuestion(data.question.id));
		}

		if (type === 'revert' && id === selectedQuestion.id) {
			yield put(quizEditorActions.getQuizQuestion(data.question.id, true));
		}

		if (type === 'remove' && !!data.question && id === selectedQuestion.id) {
			yield put(quizEditorActions.selectQuestion(data.question));
		}

		yield put(quizEditorActions.setUpdateTime(new Date()));

		yield put(
			notificationActions.createNotification(
				i18n.t(`admin_quiz_editor.manage_confirmation_message.${type}`),
			),
		);
		yield put(
			quizEditorActions.createQuizQuestionSuccess({
				...prevQuizData,
				questions: newQuestionsList,
			}),
		);

		if (callback) callback();

		yield put(quizEditorActions.manageQuizQuestionSuccess());
	} catch (e) {
		const { response } = e;
		const { data } = response || {};
		yield put(
			errorHandlerActions.handleError(e, 'notification', {
				code: 422,
				message: data.error,
			}),
		);
		yield put(quizEditorActions.manageQuizQuestionError());
	}
}

export function* handleUpdateQuizQuestionRequest({ payload }) {
	const { blockUpdate, loadingPublish } = yield select(quizFullData);

	if (blockUpdate || loadingPublish) return;

	try {
		const { id, formData, index, updateSideEffect } = payload || {};

		const prevQuizData = yield select(quizData);
		const filteredQuestions = prevQuizData.questions?.filter((item) => !item.hide);
		const selectedQuestion = yield select(selectedQuestionData);

		const { data } = yield call(processRequest, `questions/${id}`, 'PATCH', {
			question: formData,
		});

		const newQuestionsList =
			prevQuizData.questions &&
			(index !== undefined
				? prevQuizData.questions.filter((question) => question.id !== id)
				: prevQuizData.questions.map((question) =>
						question.id === id ? data.question : question,
				  ));

		yield put(quizEditorActions.updateQuestionsList(newQuestionsList));

		if (!updateSideEffect) {
			if (index !== undefined) {
				const filteredNewQuestionsList = newQuestionsList.filter((item) => !item.hide);
				const formattedList = filteredNewQuestionsList.length
					? filteredQuestions
					: newQuestionsList;

				if (selectedQuestion.id === id && newQuestionsList.length) {
					yield put(
						quizEditorActions.getQuizQuestion(
							formattedList[getFilteredIndex(formattedList, id) + 1]
								? formattedList[getFilteredIndex(formattedList, id) + 1].id
								: formattedList[getFilteredIndex(formattedList, id) - 1].id,
						),
					);
				}
			} else if (formData.type) {
				yield put(quizEditorActions.getQuizQuestion(data.question.id, true));
			} else {
				const updatedSelectedQuestion = yield select(selectedQuestionData);
				if (updatedSelectedQuestion.id === id) {
					yield put(quizEditorActions.selectQuestion(data.question));
				}
			}
		}

		yield put(quizEditorActions.updateQuizQuestionSuccess(new Date()));

		if (formData.exercise_stage) {
			yield put(
				notificationActions.createNotification(
					i18n.t(
						`admin_quiz_editor.manage_confirmation_message.${formData.exercise_stage}`,
					),
				),
			);
		}
	} catch (e) {
		yield put(
			errorHandlerActions.handleError(
				e,
				e.response.status === 404 ? 'ignore' : 'notification',
			),
		);
		yield put(quizEditorActions.updateQuizQuestionError());
	}
}

export function* handlePreviewQuizRequest({ payload }) {
	try {
		const { quizId, questionId, streamId, type } = payload || {};

		const { data } = yield call(processRequest, 'quizzes/preview', 'GET', {
			id: quizId,
			type: getStageByType(type),
			stream_id: streamId,
			question_id: questionId,
		});

		yield call(historyPush, `/exercise/${quizId}/preview`);
		yield put(nextExerciseDetailsActions.createExerciseSuccess(data));

		yield put(quizEditorActions.previewQuizSuccess());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(quizEditorActions.publishQuizError());
	}
}

export function* handlePublishQuizRequest({ payload }) {
	try {
		const { params } = payload || {};
		const { id, type } = params || {};

		yield call(processRequest, 'quizzes/publish', 'POST', {
			id,
			type: getStageByType(type),
		});
		yield put(
			notificationActions.createNotification(
				i18n.t('admin_quiz_editor.publish_notification'),
			),
		);

		yield call(historyPush, `/quizzes/${id}`);
		yield put(quizEditorActions.publishQuizSuccess());
		yield put(quizEditorActions.clearState());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(quizEditorActions.publishQuizError());
	}
}
